import { cn } from '@/lib/utils'
import { type LucideIcon, ShieldQuestion } from 'lucide-react'
import type React from 'react'
import { Button } from './button'
import { StateIcon } from './state-icon'

export interface TabContent {
	title: string
	description: string
	/** Optional: Override the action button for this specific tab */
	action?: {
		label: string
		onClick: () => void
		icon?: React.ReactNode
	}
	/** Optional: Override the secondary action for this specific tab */
	secondaryAction?: {
		label: string
		onClick: () => void
	}
	/** Optional: Additional action buttons (rendered as secondary buttons) */
	additionalActions?: Array<{
		label: string
		onClick: () => void
		icon?: React.ReactNode
	}>
}

interface EmptyStateProps {
	icon: LucideIcon
	title?: string
	description?: string
	action?: {
		label: string
		onClick: () => void
		icon?: React.ReactNode
		dataTestId?: string
	}
	secondaryAction?: {
		label: string
		onClick: () => void
		dataTestId?: string
	}
	/** Additional action buttons (rendered as secondary buttons) */
	additionalActions?: Array<{
		label: string
		onClick: () => void
		icon?: React.ReactNode
		dataTestId?: string
	}>
	/** Additional CSS classes for the container */
	className?: string
	/** Container height (default: 400px) */
	containerHeight?: number | string
	/** Show border (default: true) */
	showBorder?: boolean
	dataTestId?: string
	/** Tab-specific content mapped by tab key (e.g., { all: {...}, active: {...}, inactive: {...} }) */
	tabContents?: Record<string, TabContent>
	/** Current active tab key (used with tabContents) */
	currentTab?: string
	/**
	 * A dead end the user made with their own filters or search, rather than a milestone. Drops the
	 * icon bubble and tightens the spacing, so it does not read like "you have not created one yet".
	 */
	variant?: 'milestone' | 'no-match'
	/**
	 * Why the list may look empty for this role, from the RBAC grants. A footnote under the actions,
	 * not a replacement for the description: the roles that see it are the ones that most need to be
	 * told what the feature is for.
	 */
	accessNote?: string
}

export function EmptyState({
	icon: Icon,
	title: providedTitle,
	description: providedDescription,
	action: providedAction,
	secondaryAction: providedSecondaryAction,
	additionalActions: providedAdditionalActions,
	className,
	containerHeight = 400,
	showBorder = true,
	tabContents,
	currentTab,
	accessNote,
	variant = 'milestone',
	dataTestId,
}: EmptyStateProps) {
	const height = typeof containerHeight === 'number' ? `${containerHeight}px` : containerHeight

	// Determine content based on tab or provided props
	let title = providedTitle || 'No data available'
	let description = providedDescription || ''
	let action = providedAction
	let secondaryAction = providedSecondaryAction
	let additionalActions = providedAdditionalActions

	// If tabContents is provided and currentTab matches, use tab-specific content
	if (tabContents && currentTab && tabContents[currentTab]) {
		const tabContent = tabContents[currentTab]
		title = tabContent.title
		description = tabContent.description
		// Tab-specific action overrides provided action
		action = tabContent.action || providedAction
		secondaryAction = tabContent.secondaryAction || providedSecondaryAction
		additionalActions = tabContent.additionalActions || providedAdditionalActions
	}

	return (
		<div
			data-testid={dataTestId}
			className={cn(
				'flex items-center justify-center bg-card',
				showBorder && 'border border-dashed rounded-lg bg-card',
				className,
			)}
			style={{ height }}>
			{/*
			 * Spacing lives on the column, not on each child, so a state with no action carries no
			 * trailing margin: that margin used to sit under the text and push the whole block above
			 * the optical centre of the panel. The column is `max-w-md`, which is wide enough for a short
			 * description to sit on one line: a sentence broken mid phrase reads worse than a long one.
			 */}
			<div
				className={cn(
					'flex flex-col items-center text-center px-8 py-10 max-w-md',
					variant === 'milestone' ? 'gap-5' : 'gap-3',
				)}>
				{/*
				 * What the state says is one group: the mark, the heading and the sentence under it. The
				 * action is the next step, so it sits further away than the parts of the message sit from
				 * each other.
				 */}
				<div className='flex flex-col items-center gap-4'>
					{variant === 'milestone' && <StateIcon icon={Icon} />}

					{/* A state panel is the only thing on screen, so its heading is the one 20px in the app. */}
					<div className='flex flex-col gap-2'>
						<h3 className='text-state-title leading-tight font-semibold text-foreground text-balance'>{title}</h3>
						{description && (
							<p className='text-company-name text-muted-foreground/90 leading-relaxed text-pretty'>{description}</p>
						)}
					</div>
				</div>

				{(action || additionalActions) && (
					/*
					 * One row, wrapping only when it has to: a second action is an alternative route to the
					 * same goal, not a step after the first. `[&_svg]:mr-0` drops the margin callers put on
					 * their icon, so the button's own gap sets the spacing, as it does in the page header.
					 */
					<div className='flex flex-wrap items-center justify-center gap-2 [&_svg]:mr-0'>
						{action && (
							<Button onClick={action.onClick} className='text-control' data-testid={action.dataTestId}>
								{action.icon}
								{action.label}
							</Button>
						)}

						{additionalActions?.map(additionalAction => (
							<Button
								key={additionalAction.label}
								onClick={additionalAction.onClick}
								variant='outline'
								className='text-control'
								data-testid={additionalAction.dataTestId}>
								{additionalAction.icon}
								{additionalAction.label}
							</Button>
						))}
					</div>
				)}

				{/* Footnotes sit together and quieter than the message, so the action keeps the eye. */}
				{(accessNote || secondaryAction) && (
					<div className='flex flex-col items-center gap-2'>
						{accessNote && (
							<p
								className='text-control text-muted-foreground/70 flex items-start justify-center gap-1.5 text-pretty'
								data-testid='empty-state-access-note'>
								<ShieldQuestion className='size-(--icon-standalone) shrink-0 mt-px' aria-hidden='true' />
								<span>{accessNote}</span>
							</p>
						)}

						{secondaryAction && (
							<div className='text-control'>
								<span className='text-muted-foreground/70 text-control'>Need help? </span>
								<Button
									variant={'link'}
									className='text-control text-primary hover:underline font-medium px-1 py-0 h-fit'
									onClick={secondaryAction.onClick}
									data-testid={secondaryAction.dataTestId}>
									{secondaryAction.label}
								</Button>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	)
}
